import { Dropdown, Field, Input, makeStyles, mergeClasses, Option } from '@fluentui/react-components';
import { Contrast, DarkMode, GridView, LightMode, ViewList, Visibility, VisibilityOff } from '@material-symbols-svg/react/rounded/w500';
import { useState } from 'react';

import { SectionLabel } from '../components';
import { type ThemeMode, usePreferences, type ViewMode } from '../preferences';
import { palette, tokens, useSurfaceStyles } from '../theme';
import { ORG_OPTIONS, PROJECT_OPTIONS, Settings } from './models';
import { PatInstructions } from './PatInstructions';
import { SegmentedToggle } from './SegmentedToggle';
import { useSettings } from './SettingsProvider';

export const SettingsView: React.FC = () => {
    const classes = useStyles();
    const surfaces = useSurfaceStyles();
    const { settings, save } = useSettings();
    const { preferences, setDefaultView, setThemeMode } = usePreferences();

    const [patVisible, setPatVisible] = useState(false);

    const update = <K extends keyof Settings>(key: K, value: Settings[K]) => save({ ...settings, [key]: value });

    return (
        <section className={classes.root}>
            <div className={classes.scroll}>
                <article className={mergeClasses(surfaces.card, classes.sectionCard)}>
                    <header className={classes.sectionHeader}>
                        <h2 className={classes.sectionTitle}>General</h2>
                        <p className={classes.sectionSubtitle}>Preferences saved locally on this device.</p>
                    </header>

                    <div className={classes.generalBody}>
                        <PreferenceRow label="Default view" description="How Team and Report cards are laid out by default.">
                            <SegmentedToggle<ViewMode>
                                value={preferences.defaultView}
                                onChange={setDefaultView}
                                options={[
                                    { value: 'grid', label: 'Grid', icon: <GridView className={classes.toggleIcon} aria-hidden /> },
                                    { value: 'list', label: 'List', icon: <ViewList className={classes.toggleIcon} aria-hidden /> },
                                ]}
                                ariaLabel="Default view"
                            />
                        </PreferenceRow>

                        <PreferenceRow label="Appearance" description="Follow your system, or pick light or dark.">
                            <SegmentedToggle<ThemeMode>
                                value={preferences.themeMode}
                                onChange={setThemeMode}
                                options={[
                                    {
                                        value: 'system',
                                        label: 'System',
                                        icon: <Contrast className={classes.toggleIcon} aria-hidden />,
                                    },
                                    { value: 'light', label: 'Light', icon: <LightMode className={classes.toggleIcon} aria-hidden /> },
                                    { value: 'dark', label: 'Dark', icon: <DarkMode className={classes.toggleIcon} aria-hidden /> },
                                ]}
                                ariaLabel="Appearance"
                            />
                        </PreferenceRow>
                    </div>
                </article>

                <article className={mergeClasses(surfaces.card, classes.sectionCard)}>
                    <header className={classes.sectionHeader}>
                        <h2 className={classes.sectionTitle}>Azure DevOps</h2>
                        <p className={classes.sectionSubtitle}>Connection used to read and write your work items.</p>
                    </header>

                    <div className={classes.adoBody}>
                        <section className={classes.leftColumn}>
                            <SectionLabel text="Connection" />
                            <div className={classes.fields}>
                                <Field label="Personal Access Token (PAT)" required>
                                    <Input
                                        type={patVisible ? 'text' : 'password'}
                                        value={settings.pat}
                                        placeholder="your-pat-here"
                                        onChange={(_, data) => update('pat', data.value)}
                                        contentAfter={
                                            <button
                                                type="button"
                                                className={classes.eyeButton}
                                                aria-label={patVisible ? 'Hide PAT' : 'Show PAT'}
                                                onClick={() => setPatVisible((v) => !v)}
                                            >
                                                {patVisible ? (
                                                    <VisibilityOff className={classes.eyeIcon} />
                                                ) : (
                                                    <Visibility className={classes.eyeIcon} />
                                                )}
                                            </button>
                                        }
                                    />
                                </Field>

                                <Field label="Organisation" required>
                                    <Dropdown
                                        value={settings.org}
                                        selectedOptions={[settings.org]}
                                        onOptionSelect={(_, data) => {
                                            if (data.optionValue) {
                                                update('org', data.optionValue);
                                            }
                                        }}
                                    >
                                        {ORG_OPTIONS.map((o) => (
                                            <Option key={o} value={o}>
                                                {o}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>

                                <Field label="Project" required>
                                    <Dropdown
                                        value={settings.project}
                                        selectedOptions={[settings.project]}
                                        onOptionSelect={(_, data) => {
                                            if (data.optionValue) {
                                                update('project', data.optionValue);
                                            }
                                        }}
                                    >
                                        {PROJECT_OPTIONS.map((p) => (
                                            <Option key={p} value={p}>
                                                {p}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </Field>
                            </div>
                        </section>

                        <PatInstructions />
                    </div>
                </article>
            </div>
        </section>
    );
};

type PreferenceRowProps = { label: string; description: string; children: React.ReactNode };
const PreferenceRow: React.FC<PreferenceRowProps> = ({ label, description, children }) => {
    const classes = useStyles();
    return (
        <div className={classes.prefRow}>
            <div className={classes.prefText}>
                <span className={classes.prefLabel}>{label}</span>
                <span className={classes.prefDescription}>{description}</span>
            </div>
            <div className={classes.prefControl}>{children}</div>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
    },
    scroll: {
        flexGrow: 1,
        overflowY: 'auto',
        minHeight: 0,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    sectionCard: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    sectionTitle: {
        margin: 0,
        fontSize: '17px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    sectionSubtitle: {
        margin: 0,
        fontSize: '13px',
        color: palette.dimText,
    },

    adoBody: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        alignItems: 'stretch',
    },
    leftColumn: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        paddingRight: tokens.spacingHorizontalXXL,
    },
    fields: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    eyeButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: palette.dimText,
        '&:hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    eyeIcon: {
        width: '18px',
        height: '18px',
    },

    generalBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    prefRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalL,
        padding: `${tokens.spacingVerticalM} 0`,
        borderBottom: `1px solid ${palette.cardBorder}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    prefText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        minWidth: 0,
    },
    prefLabel: {
        fontSize: '14px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    prefDescription: {
        fontSize: '13px',
        color: palette.dimText,
    },
    prefControl: {
        flexShrink: 0,
    },

    toggleIcon: {
        width: '16px',
        height: '16px',
    },
});
